/**
 * Deterministic Resume-Job Matching Logic
 * 
 * Scoring Breakdown (Total 100%):
 * 🧠 Skill keyword overlap -> 40%
 * 💼 Job title similarity -> 20%
 * 🎓 Education match -> 15%
 * 📈 Experience level match -> 15%
 * 📍 Location preference -> 10%
 */

// Common tech keywords to extract if not provided explicitly
const TECH_KEYWORDS = [
    'react', 'node', 'javascript', 'typescript', 'python', 'java', 'c#', 'c++', 'go', 'ruby',
    'aws', 'azure', 'gcp', 'docker', 'kubernetes', 'sql', 'nosql', 'mongodb', 'postgres',
    'redis', 'graphql', 'rest', 'api', 'html', 'css', 'tailwind', 'redux', 'next.js',
    'vue', 'angular', 'git', 'ci/cd', 'agile', 'scrum', 'machine learning', 'ai', 'data'
];

// Education keywords hierarchy
const EDUCATION_LEVELS = {
    'phd': 3,
    'doctorate': 3,
    'master': 2,
    'ms': 2,
    'bachelor': 1,
    'bs': 1,
    'degree': 1
};

// Experience level keywords
const EXPERIENCE_LEVELS = {
    'senior': 3,
    'lead': 3,
    'principal': 4,
    'architect': 4,
    'mid': 2,
    'junior': 1,
    'entry': 0,
    'intern': 0
};

export const calculateMatchScore = (job, userProfile) => {
    if (!userProfile) return { score: 0, details: {} };

    // Prepare text for analysis
    const userText = (
        (userProfile.resumeText || '') + ' ' +
        (userProfile.career || '') + ' ' +
        (userProfile.education || '')
    ).toLowerCase();

    const currentRole = (userProfile.career || '').toLowerCase(); // Approximating current role/interest

    // Prepare job text
    const jobDescription = (job.description || '').toLowerCase();
    const jobTitle = (job.title || '').toLowerCase();
    const jobTechStack = (job.technologies || []).map(t => t.toLowerCase());

    const scores = {
        skills: 0,
        title: 0,
        education: 0,
        experience: 0,
        location: 0
    };

    const explanations = [];

    // 1. 🧠 Skill Keyword Overlap (40%)
    const jobKeywords = new Set([...jobTechStack]);
    // Extract keywords from description if tech stack is empty or sparse
    TECH_KEYWORDS.forEach(tech => {
        if (jobDescription.includes(tech)) jobKeywords.add(tech);
    });

    if (jobKeywords.size > 0) {
        let matchCount = 0;
        const matchedSkills = [];

        jobKeywords.forEach(keyword => {
            if (userText.includes(keyword)) {
                matchCount++;
                matchedSkills.push(keyword);
            }
        });

        // Score formulation: Match 50% of keywords = 100% of this category (generous scoring)
        const coverage = Math.min(matchCount / (jobKeywords.size * 0.5), 1);
        scores.skills = Math.round(coverage * 40);

        if (matchedSkills.length > 0) {
            explanations.push(`Matches ${matchedSkills.length} key skills: ${matchedSkills.slice(0, 3).join(', ')}${matchedSkills.length > 3 ? '...' : ''}`);
        }
    } else {
        // Fallback if no keywords found to match against
        scores.skills = 20; // Give benefit of doubt
    }

    // 2. 💼 Job Title Similarity (20%)
    if (currentRole) {
        const titleTokens = jobTitle.split(/[\s-]+/);
        const roleTokens = currentRole.split(/[\s-]+/);

        const matchingTokens = titleTokens.filter(token =>
            token.length > 3 && roleTokens.includes(token)
        );

        if (matchingTokens.length > 0) {
            scores.title = 20;
            explanations.push(`Title matches interest: "${currentRole}"`);
        } else if (jobTitle.includes(currentRole) || currentRole.includes(jobTitle)) {
            scores.title = 20;
            explanations.push(`Title aligns with "${currentRole}"`);
        }
    }

    // 3. 🎓 Education Match (15%)
    // Find highest education in Job
    let jobEduLevel = 0;
    Object.entries(EDUCATION_LEVELS).forEach(([key, lvl]) => {
        if (jobDescription.includes(key)) jobEduLevel = Math.max(jobEduLevel, lvl);
    });

    // Find highest education in User
    let userEduLevel = 0;
    Object.entries(EDUCATION_LEVELS).forEach(([key, lvl]) => {
        if (userText.includes(key)) userEduLevel = Math.max(userEduLevel, lvl);
    });

    if (userEduLevel >= jobEduLevel) {
        scores.education = 15;
        if (jobEduLevel > 0) explanations.push("Education requirements met");
    } else {
        // Partial credit for having some degree
        if (userEduLevel > 0) scores.education = 10;
    }

    // 4. 📈 Experience Level Match (15%)
    let jobExpLevel = 2; // Default to mid
    Object.entries(EXPERIENCE_LEVELS).forEach(([key, lvl]) => {
        if (jobTitle.includes(key) || jobDescription.includes(key)) jobExpLevel = lvl;
    });

    // Heuristics for years in resume (very rough)
    const yearsMatch = userText.match(/(\d+)\+?\s*years?/);
    let userYears = yearsMatch ? parseInt(yearsMatch[1]) : 0;
    let userExpLevel = userYears > 5 ? 3 : (userYears > 2 ? 2 : 1);

    // Adjust logic: if job is Junior (1) and user is Senior (3), that's a match (overqualified is fine)
    // If job is Senior (3) and user is Junior (1), penalize
    if (userExpLevel >= jobExpLevel) {
        scores.experience = 15;
        explanations.push("Experience level fits");
    } else if (userExpLevel >= jobExpLevel - 1) {
        scores.experience = 10; // Close enough
    } else {
        scores.experience = 5; // Stretch goal
    }

    // 5. 📍 Location Preference (10%)
    // Since we don't have explicit user location preference in profile object typically, 
    // we check if they mentioned it in resume text or if it matches their current profile location (if added later)
    // For now, assume neutral/positive default unless explicitly remote
    if (job.location && typeof job.location === 'string' && job.location.toLowerCase().includes('remote')) {
        scores.location = 10;
        explanations.push("Remote friendly");
    } else {
        scores.location = 5; // Default points
    }

    const totalScore = Math.min(
        100,
        scores.skills + scores.title + scores.education + scores.experience + scores.location
    );

    return {
        score: Math.round(totalScore),
        breakdown: scores,
        explanations: explanations,
        level: totalScore >= 70 ? 'Strong' : (totalScore >= 40 ? 'Medium' : 'Low')
    };
};
